'use client';

/**
 * HandoffTimeoutCallback — "request a call-back" sub-view of the timeout
 * recovery flow (see handoff-timeout-panel.tsx for the view machine this
 * composes into).
 *
 * Owns its own phone/country-code/time-window form state locally — same
 * rationale as handoff-timeout-message.tsx's textarea: a failed submit
 * leaves this component mounted (container's view stays CALLBACK), so the
 * typed number/selection survives a retry without extra plumbing.
 *
 * Two copy variants keyed on whether a phone is already on file
 * (Decision #17's optional `phone` contact field):
 *   - No phone on file → the input row is the ONLY option, shown
 *     immediately ("What number can we call?").
 *   - Phone on file → do NOT ask again. Show the known number as a plain
 *     line with a "Change" affordance; the input row only reveals if the
 *     user actually wants a different number ("Can we call you on this
 *     number?").
 */
import { useState } from 'react';
import { ChevronLeft, Lock, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StandardCard } from './primitives/standard-card';
import { CallbackWindow } from '@/lib/api/support-requests';

const CALLBACK_WINDOWS: { value: CallbackWindow; label: string }[] = [
  { value: CallbackWindow.ANYTIME, label: 'Anytime' },
  { value: CallbackWindow.MORNING, label: 'Morning' },
  { value: CallbackWindow.AFTERNOON, label: 'Afternoon' },
  { value: CallbackWindow.EVENING, label: 'Evening' },
];

/** Small, deliberately non-exhaustive set — a full ITU country-code list is
 *  overkill for a US-site call-back form. Extend if a real need shows up;
 *  this isn't meant to be a general-purpose phone-input primitive. */
const COUNTRY_CODES = ['+1', '+44', '+91', '+61', '+971'];

interface HandoffTimeoutCallbackProps {
  /** Phone already on file from the pre-handoff contact form, if the user
   *  provided one. Presence alone drives the two-variant copy above. */
  phoneOnFile?: string;
  onBack: () => void;
  onSubmit: (phone: string, callbackWindow: CallbackWindow) => void;
  submitting: boolean;
  error?: string | null;
}

export function HandoffTimeoutCallback({
  phoneOnFile,
  onBack,
  onSubmit,
  submitting,
  error,
}: HandoffTimeoutCallbackProps) {
  // No phone on file → editing starts true (the input row IS the form).
  // Phone on file → editing starts false (show the known number + Change).
  const [editing, setEditing] = useState(!phoneOnFile);
  // US site default — the Figma mock shows +91, but +1 is correct for this
  // audience. Deliberate divergence from the mock, not an oversight.
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState(phoneOnFile ?? '');
  const [callbackWindow, setCallbackWindow] = useState<CallbackWindow>(
    CallbackWindow.ANYTIME
  );

  // While showing the known number (not editing), the on-file value IS the
  // value that will be submitted — it's already a complete string as
  // originally captured, so it's sent verbatim rather than re-parsed
  // through the country-code selector.
  const activePhone = editing ? phoneNumber.trim() : (phoneOnFile ?? '');
  const canSubmit = activePhone.length > 0 && !submitting;

  const handleSubmit = () => {
    const phone = editing
      ? `${countryCode} ${phoneNumber.trim()}`.trim()
      : activePhone;
    onSubmit(phone, callbackWindow);
  };

  return (
    <StandardCard
      icon={<Phone aria-hidden="true" className="size-[15px]" />}
      eyebrow="REQUEST A CALL-BACK"
      title={
        phoneOnFile
          ? 'Can we call you on this number?'
          : 'What number can we call?'
      }
    >
      <div className="flex flex-col gap-[14px]">
        <div className="text-[13.5px]" style={{ color: '#8a7c68' }}>
          {phoneOnFile
            ? "This is the number you shared earlier — we'll use it for this call-back unless you'd like to change it."
            : "We don't have a phone number on file yet. Add one and a guide will call you back as soon as they're free."}
        </div>

        {phoneOnFile && !editing ? (
          <div className="flex items-center justify-between gap-[10px]">
            <span
              className="text-[14.5px] font-semibold"
              style={{ color: '#31364E' }}
            >
              {phoneOnFile}
            </span>
            <button
              type="button"
              onClick={() => setEditing(true)}
              aria-label="Change phone number"
              className="text-[13px] font-semibold text-[#E36001] underline-offset-2 hover:underline"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="flex gap-[8px]">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              disabled={submitting}
              aria-label="Country code"
              className="rounded-[11px] border-[1px] border-[#e6dccd] bg-[#FEFCF9] px-[14px] py-[11px] text-[14.5px] text-[#31364e] disabled:opacity-50"
            >
              {COUNTRY_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={submitting}
              placeholder="Phone number"
              aria-label="Phone number"
              className="flex-1 rounded-[11px] border-[1px] border-[#e6dccd] bg-[#FEFCF9] px-[14px] py-[11px] text-[14.5px] text-[#31364e] placeholder:text-[#b3a996] disabled:opacity-50"
            />
          </div>
        )}

        <div className="flex flex-col gap-[8px]">
          <span
            className="text-[12.5px] font-semibold"
            style={{ color: '#8a7c68' }}
          >
            Best time to call
          </span>
          <div className="flex flex-wrap gap-[8px]">
            {CALLBACK_WINDOWS.map((w) => {
              const selected = w.value === callbackWindow;
              return (
                <button
                  key={w.value}
                  type="button"
                  onClick={() => setCallbackWindow(w.value)}
                  aria-pressed={selected}
                  className={cn(
                    'rounded-[20px] px-[13px] py-[7px] text-[12.5px] font-semibold',
                    selected
                      ? 'border-[1px] border-[#E8C7A0] bg-[#FFF8EF] text-[#31364E]'
                      : 'border-[1px] border-[#eadfce] bg-white text-[#5b6072]'
                  )}
                >
                  {w.label}
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="flex items-center gap-[6px] text-[12px]"
          style={{ color: '#9a8f7f' }}
        >
          <Lock aria-hidden="true" className="size-[11px]" />
          Only used for this call-back.
        </div>

        {error && (
          <div
            role="alert"
            className="text-[12.5px]"
            style={{ color: '#b3261e' }}
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            className="flex items-center gap-1 text-[14px] font-semibold text-[#8a7c68] disabled:opacity-50"
          >
            <ChevronLeft aria-hidden="true" className="size-[14px]" />
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
              'rounded-[22px] px-[18px] py-[10px] text-[13.5px] font-semibold text-white',
              'shadow-[0_10px_22px_-12px_rgba(227,96,1,0.7)]',
              canSubmit ? 'bg-[#E36001]' : 'cursor-not-allowed bg-[#E36001]/60'
            )}
          >
            {submitting ? 'Confirming…' : 'Confirm call-back'}
          </button>
        </div>
      </div>
    </StandardCard>
  );
}
